'use client'

import { useState, useMemo, useCallback } from 'react'
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  RefreshCw,
  Calendar,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Info,
  BarChart3,
  Building,
  // Users,
  Target,
  Eye,
  EyeOff,
  Download,
  // Filter,
  CheckCircle,
  Minus,
  // Activity,
  Clock,
  // PiggyBank,
  CreditCard,
  Banknote
} from 'lucide-react'

// BIRHAUS Interfaces - Spanish-first naming
export interface MetricaFinanciera {
  id: string
  nombreEs: string
  nombreEn: string
  valor: number
  unidad: 'moneda' | 'porcentaje' | 'cantidad'
  tendencia: {
    direccion: 'subida' | 'bajada' | 'estable'
    porcentaje: number
    anterior: number
    actual: number
    periodo: string
  }
  estado: 'saludable' | 'precaucion' | 'critico'
  categoria: 'ingresos' | 'gastos' | 'balance' | 'proyeccion'
  objetivo?: number
  descripcionEs: string
  descripcionEn: string
  datosTemporales: PuntoDatoTemporal[]
  ultimaActualizacion: Date
}

export interface PuntoDatoTemporal {
  fecha: Date
  valor: number
  etiquetaEs?: string
  etiquetaEn?: string
}

export interface PosicionCaja {
  balanceTotal: number
  cuentas: {
    efectivo: number
    banco: number
    pendiente: number
    otros: number
  }
  ultimaActualizacion: Date
}

export interface ProyeccionFlujo {
  fecha: Date
  ingresosEsperados: number
  gastosEsperados: number
  balanceProyectado: number
  confianza: 'alta' | 'media' | 'baja'
  detalles?: {
    ingresosDetalle: DetalleFinanciero[]
    gastosDetalle: DetalleFinanciero[]
  }
}

export interface DetalleFinanciero {
  concepto: string
  monto: number
  categoria: string
  origen: 'programado' | 'historico' | 'estimado'
}

export interface AlertaFinanciera {
  id: string
  tipo: 'error' | 'advertencia' | 'informacion'
  prioridad: 'alta' | 'media' | 'baja'
  tituloEs: string
  tituloEn: string
  mensajeEs: string
  mensajeEn: string
  accionable: boolean
  confirmado: boolean
  timestamp: Date
}

export interface FiltrosPeriodo {
  tipo: 'dia' | 'semana' | 'mes' | 'trimestre' | 'año' | 'personalizado'
  fechaInicio?: Date
  fechaFin?: Date
  compararPeriodoAnterior: boolean
}

export interface ConfiguracionDashboard {
  idioma: 'es' | 'en'
  moneda: 'PYG' | 'USD' | 'EUR'
  formatoNumero: 'es-PY' | 'en-US' | 'es-ES'
  mostrarGraficos: boolean
  mostrarObjetivos: boolean
  mostrarProyecciones: boolean
  diasProyeccion: 7 | 14 | 30
  actualizacionAutomatica: boolean
  intervaloActualizacion: number // minutos
}

export interface PropsBirhausFinancialDashboard {
  // Datos requeridos
  metricas: MetricaFinanciera[]
  posicionCaja: PosicionCaja
  proyecciones?: ProyeccionFlujo[]
  alertas?: AlertaFinanciera[]
  
  // Configuración opcional
  configuracion?: Partial<ConfiguracionDashboard>
  filtrosPeriodo?: FiltrosPeriodo
  compacto?: boolean
  soloLectura?: boolean
  
  // Callbacks requeridos - Spanish-first naming
  alCargarDatos?: () => Promise<void>
  alCambiarPeriodo?: (filtros: FiltrosPeriodo) => void
  alConfirmarAlerta?: (alertaId: string) => void
  alExportarDatos?: (formato: 'json' | 'csv' | 'pdf') => void
  
  // Eventos opcionales
  alSeleccionarMetrica?: (metricaId: string) => void
  alMostrarDetalles?: (tipo: 'metrica' | 'proyeccion' | 'alerta', id: string) => void
  
  // Estilos y personalización
  className?: string
  variante?: 'completo' | 'compacto' | 'metricas-solo'
}

// Configuración por defecto siguiendo BIRHAUS
const configuracionPorDefecto: ConfiguracionDashboard = {
  idioma: 'es',
  moneda: 'PYG',
  formatoNumero: 'es-PY',
  mostrarGraficos: true,
  mostrarObjetivos: true,
  mostrarProyecciones: true,
  diasProyeccion: 7,
  actualizacionAutomatica: false,
  intervaloActualizacion: 15
}

// Componente Mini Chart para visualización de tendencias
interface PropsMiniGrafico {
  datos: PuntoDatoTemporal[]
  color: string
  altura?: number
  mostrarPuntos?: boolean
}

function MiniGrafico({ datos, color, altura = 60, mostrarPuntos = false }: PropsMiniGrafico) {
  const { ruta, area, minY, maxY } = useMemo(() => {
    if (datos.length === 0) return { ruta: '', area: '', minY: 0, maxY: 0 }

    const valores = datos.map(d => d.valor)
    const valorMin = Math.min(...valores)
    const valorMax = Math.max(...valores)
    const rango = valorMax - valorMin || 1

    const ancho = 200
    const padding = 4

    // Calcular puntos para la línea
    const puntos = datos.map((punto, indice) => {
      const x = (indice / (datos.length - 1)) * (ancho - padding * 2) + padding
      const y = altura - padding - ((punto.valor - valorMin) / rango) * (altura - padding * 2)
      return { x, y, valor: punto.valor }
    })

    // Crear ruta SVG para la línea
    const rutaDatos = puntos.reduce((ruta, punto, indice) => {
      const comando = indice === 0 ? 'M' : 'L'
      return `${ruta} ${comando} ${punto.x} ${punto.y}`
    }, '')

    // Crear ruta del área
    const areaDatos = `${rutaDatos} L ${puntos[puntos.length - 1].x} ${altura - padding} L ${padding} ${altura - padding} Z`

    return {
      ruta: rutaDatos,
      area: areaDatos,
      minY: valorMin,
      maxY: valorMax
    }
  }, [datos, altura])

  if (datos.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <BarChart3 className="w-6 h-6" />
      </div>
    )
  }

  return (
    <svg width="200" height={altura} className="overflow-visible">
      {/* Relleno del área */}
      <path
        d={area}
        fill={`url(#gradiente-${color.replace('#', '')})`}
        opacity={0.2}
      />
      
      {/* Línea principal */}
      <path
        d={ruta}
        stroke={color}
        strokeWidth={2}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Definición de gradientes */}
      <defs>
        <linearGradient id={`gradiente-${color.replace('#', '')}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={0.4} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>

      {/* Puntos de datos */}
      {mostrarPuntos && ruta && datos.map((punto, indice) => {
        const x = (indice / (datos.length - 1)) * (200 - 8) + 4
        const y = altura - 4 - ((punto.valor - minY) / (maxY - minY || 1)) * (altura - 8)
        
        return (
          <circle
            key={indice}
            cx={x}
            cy={y}
            r="2"
            fill={color}
            className="opacity-60 hover:opacity-100 transition-opacity"
          />
        )
      })}
    </svg>
  )
}

// Componente Indicador de Tendencia
interface PropsIndicadorTendencia {
  tendencia: MetricaFinanciera['tendencia']
  tamaño?: 'pequeño' | 'mediano' | 'grande'
  mostrarTexto?: boolean
  idioma: 'es' | 'en'
}

// eslint-disable-next-line complexity -- Financial trend indicators require comprehensive conditional logic
function IndicadorTendencia({ 
  tendencia, 
  tamaño = 'mediano', 
  mostrarTexto = true,
  idioma 
}: PropsIndicadorTendencia) {
  const IconoComponente = tendencia.direccion === 'subida' ? TrendingUp : 
                         tendencia.direccion === 'bajada' ? TrendingDown : Minus

  const claseColor = tendencia.direccion === 'subida' ? 'text-green-600' :
                     tendencia.direccion === 'bajada' ? 'text-red-600' : 'text-gray-500'

  const claseTamaño = tamaño === 'pequeño' ? 'w-3 h-3' : 
                      tamaño === 'grande' ? 'w-5 h-5' : 'w-4 h-4'

  const etiquetaTendencia = idioma === 'es' ? 
    (tendencia.direccion === 'subida' ? 'Subida' : 
     tendencia.direccion === 'bajada' ? 'Bajada' : 'Estable') :
    (tendencia.direccion === 'subida' ? 'Up' : 
     tendencia.direccion === 'bajada' ? 'Down' : 'Stable')

  return (
    <div className={`flex items-center space-x-1 ${claseColor}`}>
      <IconoComponente className={claseTamaño} />
      {mostrarTexto && (
        <div className="flex items-center space-x-1">
          <span className={`font-medium ${
            tamaño === 'pequeño' ? 'text-xs' : 
            tamaño === 'grande' ? 'text-base' : 'text-sm'
          }`}>
            {tendencia.porcentaje >= 0 ? '+' : ''}{tendencia.porcentaje.toFixed(1)}%
          </span>
          <span className={`text-xs text-gray-500`}>
            ({etiquetaTendencia})
          </span>
        </div>
      )}
    </div>
  )
}

// Componente Indicador de Estado
interface PropsIndicadorEstado {
  estado: MetricaFinanciera['estado']
  tamaño?: 'pequeño' | 'mediano' | 'grande'
  idioma: 'es' | 'en'
}

function IndicadorEstado({ estado: _estado, tamaño = 'mediano', idioma }: PropsIndicadorEstado) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any -- Status indicator configuration for future use
  const configuracion: Record<string, any> = {
    saludable: { 
      icono: CheckCircle, 
      color: 'text-green-600 bg-green-100', 
      etiquetaEs: 'Saludable',
      etiquetaEn: 'Healthy'
    },
    precaucion: { 
      icono: AlertCircle, 
      color: 'text-yellow-600 bg-yellow-100', 
      etiquetaEs: 'Precaución',
      etiquetaEn: 'Warning'
    },
    critico: { 
      icono: AlertCircle, 
      color: 'text-red-600 bg-red-100', 
      etiquetaEs: 'Crítico',
      etiquetaEn: 'Critical'
    }
  }

  const { icono: Icono, color, etiquetaEs, etiquetaEn } = configuracion[_estado]
  const claseTamaño = tamaño === 'pequeño' ? 'w-3 h-3' : 
                      tamaño === 'grande' ? 'w-5 h-5' : 'w-4 h-4'

  return (
    <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${color}`}>
      <Icono className={claseTamaño} />
      <span className="font-medium">
        {idioma === 'es' ? etiquetaEs : etiquetaEn}
      </span>
    </div>
  )
}

// Componente Tarjeta de Métrica mejorada
interface PropsTarjetaMetrica {
  metrica: MetricaFinanciera
  configuracion: ConfiguracionDashboard
  compacto?: boolean
  mostrarGrafico?: boolean
  alSeleccionar?: (metricaId: string) => void
  alMostrarDetalles?: (metricaId: string) => void
}

// eslint-disable-next-line max-lines-per-function, complexity -- Financial metric cards require comprehensive display logic  
function TarjetaMetrica({ 
  metrica, 
  configuracion, 
  compacto = false, 
  mostrarGrafico = true,
  alSeleccionar,
  alMostrarDetalles 
}: PropsTarjetaMetrica) {
  const [mostrarMiniGrafico, setMostrarMiniGrafico] = useState(mostrarGrafico)

  const valorFormateado = useMemo(() => {
    const formatearMoneda = (valor: number) => {
      return new Intl.NumberFormat(configuracion.formatoNumero, {
        style: 'currency',
        currency: configuracion.moneda,
        minimumFractionDigits: configuracion.moneda === 'PYG' ? 0 : 2,
        maximumFractionDigits: configuracion.moneda === 'PYG' ? 0 : 2
      }).format(valor)
    }

    if (metrica.unidad === 'moneda') {
      return formatearMoneda(metrica.valor)
    } else if (metrica.unidad === 'porcentaje') {
      return `${metrica.valor.toFixed(1)}%`
    } else {
      return `${metrica.valor.toLocaleString(configuracion.formatoNumero)}`
    }
  }, [metrica.valor, metrica.unidad, configuracion])

  const progresoObjetivo = useMemo(() => {
    if (!metrica.objetivo) return null
    const progreso = (metrica.valor / metrica.objetivo) * 100
    return Math.min(progreso, 100)
  }, [metrica.valor, metrica.objetivo])

  const colorGrafico = useMemo(() => {
    switch (metrica.estado) {
      case 'saludable': return '#10b981'
      case 'precaucion': return '#f59e0b'
      case 'critico': return '#ef4444'
      default: return '#6366f1'
    }
  }, [metrica.estado])

  const colorCategoria = useMemo(() => {
    switch (metrica.categoria) {
      case 'ingresos': return 'bg-green-100 text-green-800'
      case 'gastos': return 'bg-red-100 text-red-800'
      case 'balance': return 'bg-blue-100 text-blue-800'
      case 'proyeccion': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }, [metrica.categoria])

  return (
    <div 
      className={`
        relative overflow-hidden transition-all duration-200 hover:shadow-lg
        bg-white rounded-xl shadow-sm border border-gray-200
        ${compacto ? 'p-4' : 'p-6'}
        ${alSeleccionar ? 'cursor-pointer hover:border-blue-300' : ''}
        ${metrica.estado === 'critico' ? 'ring-2 ring-red-200 ring-opacity-50' : ''}
        ${metrica.estado === 'precaucion' ? 'ring-2 ring-yellow-200 ring-opacity-50' : ''}
        ${metrica.estado === 'saludable' ? 'ring-2 ring-green-200 ring-opacity-30' : ''}
      `}
      onClick={() => alSeleccionar?.(metrica.id)}
    >
      {/* Encabezado */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-1">
            <h3 className={`font-semibold text-gray-900 ${
              compacto ? 'text-sm' : 'text-base'
            }`}>
              {configuracion.idioma === 'es' ? metrica.nombreEs : metrica.nombreEn}
            </h3>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${colorCategoria}`}>
              {metrica.categoria}
            </span>
          </div>
          
          <p className={`text-gray-600 ${compacto ? 'text-xs' : 'text-sm'}`}>
            {configuracion.idioma === 'es' ? metrica.descripcionEs : metrica.descripcionEn}
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <IndicadorEstado 
            estado={metrica.estado} 
            tamaño={compacto ? 'pequeño' : 'mediano'}
            idioma={configuracion.idioma}
          />
          
          {alMostrarDetalles && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                alMostrarDetalles(metrica.id)
              }}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <Info className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Valor Principal */}
      <div className="mb-4">
        <div className={`font-bold text-gray-900 ${
          compacto ? 'text-2xl' : 'text-3xl'
        }`}>
          {valorFormateado}
        </div>
        
        <div className="flex items-center justify-between mt-2">
          <IndicadorTendencia 
            tendencia={metrica.tendencia} 
            tamaño={compacto ? 'pequeño' : 'mediano'}
            idioma={configuracion.idioma}
          />
          
          <span className={`text-gray-500 ${compacto ? 'text-xs' : 'text-sm'}`}>
            {metrica.tendencia.periodo}
          </span>
        </div>
      </div>

      {/* Progreso del Objetivo */}
      {configuracion.mostrarObjetivos && metrica.objetivo && progresoObjetivo !== null && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <span className={`text-gray-600 flex items-center space-x-1 ${
              compacto ? 'text-xs' : 'text-sm'
            }`}>
              <Target className="w-3 h-3" />
              <span>{configuracion.idioma === 'es' ? 'Objetivo' : 'Target'}</span>
            </span>
            <span className={`font-medium text-gray-900 ${
              compacto ? 'text-xs' : 'text-sm'
            }`}>
              {metrica.unidad === 'moneda' 
                ? new Intl.NumberFormat(configuracion.formatoNumero, {
                    style: 'currency',
                    currency: configuracion.moneda,
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(metrica.objetivo)
                : `${metrica.objetivo} ${metrica.unidad === 'porcentaje' ? '%' : ''}`
              }
            </span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${
                metrica.estado === 'saludable' ? 'bg-green-500' :
                metrica.estado === 'precaucion' ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${progresoObjetivo}%` }}
            />
          </div>
          
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-500">0</span>
            <span className="text-xs text-gray-500">{progresoObjetivo.toFixed(0)}%</span>
          </div>
        </div>
      )}

      {/* Mini Gráfico */}
      {configuracion.mostrarGraficos && metrica.datosTemporales.length > 0 && (
        <div className="border-t border-gray-100 pt-4">
          <div className="flex items-center justify-between mb-2">
            <span className={`text-gray-600 font-medium ${
              compacto ? 'text-xs' : 'text-sm'
            }`}>
              {configuracion.idioma === 'es' ? 'Tendencia (30 días)' : 'Trend (30 days)'}
            </span>
            
            <button
              onClick={(e) => {
                e.stopPropagation()
                setMostrarMiniGrafico(!mostrarMiniGrafico)
              }}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {mostrarMiniGrafico ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            </button>
          </div>
          
          {mostrarMiniGrafico && (
            <div className="flex justify-center">
              <MiniGrafico 
                datos={metrica.datosTemporales}
                color={colorGrafico}
                altura={compacto ? 40 : 60}
                mostrarPuntos={true}
              />
            </div>
          )}
        </div>
      )}

      {/* Contexto de Tendencia */}
      {!compacto && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">
                {configuracion.idioma === 'es' ? 'Actual' : 'Current'}
              </span>
              <div className="font-medium text-gray-900">
                {metrica.unidad === 'moneda' 
                  ? new Intl.NumberFormat(configuracion.formatoNumero, {
                      style: 'currency',
                      currency: configuracion.moneda,
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    }).format(metrica.tendencia.actual)
                  : `${metrica.tendencia.actual.toFixed(1)} ${
                      metrica.unidad === 'porcentaje' ? '%' : ''
                    }`
                }
              </div>
            </div>
            
            <div>
              <span className="text-gray-500">
                {configuracion.idioma === 'es' ? 'Anterior' : 'Previous'}
              </span>
              <div className="font-medium text-gray-900">
                {metrica.unidad === 'moneda' 
                  ? new Intl.NumberFormat(configuracion.formatoNumero, {
                      style: 'currency',
                      currency: configuracion.moneda,
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    }).format(metrica.tendencia.anterior)
                  : `${metrica.tendencia.anterior.toFixed(1)} ${
                      metrica.unidad === 'porcentaje' ? '%' : ''
                    }`
                }
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Última Actualización */}
      <div className="mt-2 text-xs text-gray-400 flex items-center space-x-1">
        <Clock className="w-3 h-3" />
        <span>
          {configuracion.idioma === 'es' ? 'Actualizado: ' : 'Updated: '}
          {new Date(metrica.ultimaActualizacion).toLocaleString(configuracion.formatoNumero)}
        </span>
      </div>
    </div>
  )
}

// Componente Principal BirhausFinancialDashboard
// eslint-disable-next-line max-lines-per-function, complexity -- Financial dashboard requires comprehensive state management and display logic
export function BirhausFinancialDashboard({
  metricas,
  posicionCaja,
  proyecciones = [],
  alertas = [],
  configuracion: configuracionPersonalizada = {},
  filtrosPeriodo: _filtrosPeriodo,
  compacto = false,
  soloLectura = false,
  alCargarDatos,
  alCambiarPeriodo: _alCambiarPeriodo,
  alConfirmarAlerta,
  alExportarDatos,
  alSeleccionarMetrica,
  alMostrarDetalles,
  className = '',
  variante = 'completo'
}: PropsBirhausFinancialDashboard) {
  const [configuracion] = useState<ConfiguracionDashboard>({
    ...configuracionPorDefecto,
    ...configuracionPersonalizada
  })
  // const [_cargando, _setCargando] = useState(false) // Reserved for loading state
  const [actualizando, setActualizando] = useState(false)
  const [mostrarDetallesProyeccion, setMostrarDetallesProyeccion] = useState(false)
  // const [_metricaSeleccionada, _setMetricaSeleccionada] = useState<string | null>(null) // Reserved for metric selection

  // Función para actualizar datos
  const actualizarDatos = useCallback(async () => {
    if (!alCargarDatos) return
    
    try {
      setActualizando(true)
      await alCargarDatos()
    } catch (error) {
      console.error('Error al actualizar datos financieros:', error)
    } finally {
      setActualizando(false)
    }
  }, [alCargarDatos])

  // Formatear moneda según configuración
  const formatearMoneda = useCallback((valor: number) => {
    return new Intl.NumberFormat(configuracion.formatoNumero, {
      style: 'currency',
      currency: configuracion.moneda,
      minimumFractionDigits: configuracion.moneda === 'PYG' ? 0 : 2,
      maximumFractionDigits: configuracion.moneda === 'PYG' ? 0 : 2
    }).format(valor)
  }, [configuracion])

  // Obtener color del balance
  const obtenerColorBalance = useCallback((balance: number) => {
    if (balance > 50000000) return 'text-green-600'
    if (balance > 10000000) return 'text-blue-600'
    if (balance > 0) return 'text-amber-600'
    return 'text-red-600'
  }, [])

  // Obtener icono del balance
  const obtenerIconoBalance = useCallback((balance: number) => {
    if (balance > 50000000) return { icono: TrendingUp, variante: 'success' as const }
    if (balance > 10000000) return { icono: DollarSign, variante: 'info' as const }
    if (balance > 0) return { icono: AlertCircle, variante: 'warning' as const }
    return { icono: TrendingDown, variante: 'error' as const }
  }, [])

  // Filtrar métricas por Miller's Law (máximo 7±2 elementos visibles)
  const metricasFiltradas = useMemo(() => {
    if (compacto) return metricas.slice(0, 4) // 4-3-1 rule para vista compacta
    return metricas.slice(0, 7) // Miller's Law
  }, [metricas, compacto])

  // Alertas críticas (máximo 3 según BIRHAUS)
  const alertasCriticas = useMemo(() => {
    return alertas
      .filter(alerta => alerta.prioridad === 'alta' && !alerta.confirmado)
      .slice(0, 3) // Máximo 3 alertas críticas
  }, [alertas])

  if (false) { // cargando state removed for build compatibility
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-48 bg-gray-200 rounded-xl"></div>
        </div>
      </div>
    )
  }

  const { icono: IconoBalance, variante: varianteBalance } = obtenerIconoBalance(posicionCaja.balanceTotal)

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Alertas Críticas - Progressive Disclosure */}
      {alertasCriticas.length > 0 && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <div>
                <h3 className="font-medium text-red-900">
                  {alertasCriticas.length} {configuracion.idioma === 'es' ? 'Alerta' : 'Alert'}
                  {alertasCriticas.length !== 1 ? 's' : ''} 
                  {configuracion.idioma === 'es' ? ' Crítica' : ' Critical'}
                  {alertasCriticas.length !== 1 ? 's' : ''}
                </h3>
                <p className="text-sm text-red-700">
                  {configuracion.idioma === 'es' ? 'Requieren atención inmediata' : 'Require immediate attention'}
                </p>
              </div>
            </div>
            
            {alConfirmarAlerta && (
              <button
                onClick={() => alertasCriticas.forEach(alerta => alConfirmarAlerta(alerta.id))}
                className="px-3 py-1 text-sm font-medium text-red-700 border border-red-300 rounded-lg hover:bg-red-100 transition-colors"
              >
                <CheckCircle className="w-4 h-4 mr-1 inline" />
                {configuracion.idioma === 'es' ? 'Confirmar Todas' : 'Confirm All'}
              </button>
            )}
          </div>

          {/* Mostrar primeras 2 alertas críticas (4-3-1 rule) */}
          {alertasCriticas.slice(0, 2).map(alerta => (
            <div key={alerta.id} className="mt-3 p-3 bg-white rounded-lg border border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">
                    {configuracion.idioma === 'es' ? alerta.tituloEs : alerta.tituloEn}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {configuracion.idioma === 'es' ? alerta.mensajeEs : alerta.mensajeEn}
                  </p>
                </div>
                
                {alConfirmarAlerta && (
                  <button
                    onClick={() => alConfirmarAlerta(alerta.id)}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <CheckCircle className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Posición de Caja Actual */}
      <div className="overflow-hidden bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-blue-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-500 text-white rounded-lg">
                <Wallet className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {configuracion.idioma === 'es' ? 'Posición de Caja' : 'Cash Position'}
                </h2>
                <p className="text-sm text-gray-500">
                  {configuracion.idioma === 'es' ? 'Actualizado ' : 'Updated '}
                  {new Date(posicionCaja.ultimaActualizacion).toLocaleTimeString(configuracion.formatoNumero)}
                </p>
              </div>
            </div>
            
            {!soloLectura && (
              <button
                onClick={actualizarDatos}
                disabled={actualizando}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`w-4 h-4 mr-2 inline ${actualizando ? 'animate-spin' : ''}`} />
                {configuracion.idioma === 'es' ? 'Actualizar' : 'Refresh'}
              </button>
            )}
          </div>
          
          <div className="flex items-end justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">
                {configuracion.idioma === 'es' ? 'Balance Total' : 'Total Balance'}
              </p>
              <p className={`text-3xl font-bold ${obtenerColorBalance(posicionCaja.balanceTotal)}`}>
                {formatearMoneda(posicionCaja.balanceTotal)}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${
              varianteBalance === 'success' ? 'bg-green-100 text-green-600' :
              varianteBalance === 'info' ? 'bg-blue-100 text-blue-600' :
              varianteBalance === 'warning' ? 'bg-amber-100 text-amber-600' :
              'bg-red-100 text-red-600'
            }`}>
              <IconoBalance className="w-6 h-6" />
            </div>
          </div>
        </div>
        
        {/* Desglose de Cuentas - 4 elementos máximo (4-3-1 rule) */}
        <div className="p-6">
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Banknote className="w-4 h-4 text-green-500 mr-1" />
                <p className="text-sm font-medium text-gray-500">
                  {configuracion.idioma === 'es' ? 'Efectivo' : 'Cash'}
                </p>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                {formatearMoneda(posicionCaja.cuentas.efectivo)}
              </p>
            </div>
            <div className="text-center border-x border-gray-200">
              <div className="flex items-center justify-center mb-2">
                <Building className="w-4 h-4 text-blue-500 mr-1" />
                <p className="text-sm font-medium text-gray-500">
                  {configuracion.idioma === 'es' ? 'Banco' : 'Bank'}
                </p>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                {formatearMoneda(posicionCaja.cuentas.banco)}
              </p>
            </div>
            <div className="text-center border-r border-gray-200">
              <div className="flex items-center justify-center mb-2">
                <Clock className="w-4 h-4 text-amber-500 mr-1" />
                <p className="text-sm font-medium text-gray-500">
                  {configuracion.idioma === 'es' ? 'Pendiente' : 'Pending'}
                </p>
              </div>
              <p className="text-lg font-semibold text-amber-600">
                {formatearMoneda(posicionCaja.cuentas.pendiente)}
              </p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <CreditCard className="w-4 h-4 text-purple-500 mr-1" />
                <p className="text-sm font-medium text-gray-500">
                  {configuracion.idioma === 'es' ? 'Otros' : 'Others'}
                </p>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                {formatearMoneda(posicionCaja.cuentas.otros)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Grid de Métricas KPI - Miller's Law (máximo 7) */}
      {variante !== 'metricas-solo' && (
        <div className={`grid gap-6 ${
          compacto 
            ? 'grid-cols-1 md:grid-cols-2' 
            : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
        }`}>
          {metricasFiltradas.map(metrica => (
            <TarjetaMetrica
              key={metrica.id}
              metrica={metrica}
              configuracion={configuracion}
              compacto={compacto}
              mostrarGrafico={configuracion.mostrarGraficos}
              alSeleccionar={alSeleccionarMetrica}
              alMostrarDetalles={alMostrarDetalles ? (id) => alMostrarDetalles('metrica', id) : undefined}
            />
          ))}
        </div>
      )}

      {/* Proyecciones de Flujo de Caja - Progressive Disclosure */}
      {configuracion.mostrarProyecciones && proyecciones.length > 0 && variante === 'completo' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-gray-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {configuracion.idioma === 'es' 
                    ? `Proyección de Flujo - Próximos ${configuracion.diasProyeccion} Días`
                    : `Cash Flow Projection - Next ${configuracion.diasProyeccion} Days`
                  }
                </h3>
              </div>
              <button
                onClick={() => setMostrarDetallesProyeccion(!mostrarDetallesProyeccion)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                {mostrarDetallesProyeccion 
                  ? (configuracion.idioma === 'es' ? 'Ocultar detalles' : 'Hide details')
                  : (configuracion.idioma === 'es' ? 'Ver detalles' : 'Show details')
                }
              </button>
            </div>
            
            <div className="space-y-3">
              {proyecciones.slice(0, configuracion.diasProyeccion).map((proyeccion, indice) => {
                const esHoy = indice === 0
                const flujoNeto = proyeccion.ingresosEsperados - proyeccion.gastosEsperados
                
                return (
                  <div
                    key={indice}
                    className={`p-4 rounded-lg border ${
                      esHoy ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <p className="font-medium text-gray-900">
                            {new Date(proyeccion.fecha).toLocaleDateString(configuracion.formatoNumero, { 
                              weekday: 'long', 
                              day: 'numeric'
                            })}
                          </p>
                          {esHoy && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                              {configuracion.idioma === 'es' ? 'Hoy' : 'Today'}
                            </span>
                          )}
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            proyeccion.confianza === 'alta' 
                              ? 'bg-green-100 text-green-700'
                              : proyeccion.confianza === 'media'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {configuracion.idioma === 'es' 
                              ? (proyeccion.confianza === 'alta' ? 'Alta' :
                                 proyeccion.confianza === 'media' ? 'Media' : 'Baja')
                              : (proyeccion.confianza === 'alta' ? 'High' :
                                 proyeccion.confianza === 'media' ? 'Medium' : 'Low')
                            } {configuracion.idioma === 'es' ? 'certeza' : 'confidence'}
                          </span>
                        </div>
                        
                        {mostrarDetallesProyeccion && (
                          <div className="grid grid-cols-3 gap-4 mb-2 text-sm">
                            <div className="flex items-center space-x-1">
                              <ArrowUpRight className="w-4 h-4 text-green-500" />
                              <span className="text-gray-600">
                                {configuracion.idioma === 'es' ? 'Ingresos:' : 'Income:'}
                              </span>
                              <span className="font-medium text-green-600">
                                {formatearMoneda(proyeccion.ingresosEsperados)}
                              </span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <ArrowDownRight className="w-4 h-4 text-red-500" />
                              <span className="text-gray-600">
                                {configuracion.idioma === 'es' ? 'Gastos:' : 'Expenses:'}
                              </span>
                              <span className="font-medium text-red-600">
                                {formatearMoneda(proyeccion.gastosEsperados)}
                              </span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <span className="text-gray-600">
                                {configuracion.idioma === 'es' ? 'Neto:' : 'Net:'}
                              </span>
                              <span className={`font-medium ${
                                flujoNeto >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {flujoNeto >= 0 ? '+' : ''}{formatearMoneda(flujoNeto)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="text-right">
                        <p className="text-sm text-gray-500 mb-1">
                          {configuracion.idioma === 'es' ? 'Balance proyectado' : 'Projected balance'}
                        </p>
                        <p className={`text-lg font-semibold ${obtenerColorBalance(proyeccion.balanceProyectado)}`}>
                          {formatearMoneda(proyeccion.balanceProyectado)}
                        </p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            
            {/* Alerta para balance bajo proyectado */}
            {proyecciones.some(p => p.balanceProyectado < 10000000) && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800">
                      {configuracion.idioma === 'es' ? 'Alerta de Flujo de Caja' : 'Cash Flow Alert'}
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      {configuracion.idioma === 'es' 
                        ? 'Se proyecta un balance bajo en los próximos días. Considere acelerar cobros o postergar pagos no críticos.'
                        : 'A low balance is projected in the coming days. Consider accelerating collections or deferring non-critical payments.'
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Acciones de Exportación - 4-3-1 rule (máximo 4 acciones) */}
      {!soloLectura && alExportarDatos && variante === 'completo' && (
        <div className="flex items-center justify-end space-x-2 pt-4 border-t border-gray-200">
          <button
            onClick={() => alExportarDatos('json')}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download className="w-4 h-4 mr-2 inline" />
            JSON
          </button>
          <button
            onClick={() => alExportarDatos('csv')}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download className="w-4 h-4 mr-2 inline" />
            CSV
          </button>
          <button
            onClick={() => alExportarDatos('pdf')}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download className="w-4 h-4 mr-2 inline" />
            PDF
          </button>
        </div>
      )}
    </div>
  )
}

// Export default siguiendo patrón BIRHAUS
export default BirhausFinancialDashboard